import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Download, TrendingUp, Percent, BarChart3, AlertTriangle, Calendar } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend, Area, ReferenceLine, Label } from 'recharts';
import RequestButton from '@/components/RequestButton';
import { performanceData } from '@/components/PerformancePreview';
import PaperTradingResults from '@/components/PaperTradingResults';
import StylizedDivider from '@/components/StylizedDivider';

const stats = {
  delPhi: {
    monthlyReturn: 9.5,
    sharpeRatio: 1.21,
    sortinoRatio: 3.18,
    meanDailyReturn: (130.77 / 251).toFixed(2)
  },
  sp500: {
    monthlyReturn: 1.5,
    sharpeRatio: 0.62,
    sortinoRatio: 0.85,
    meanDailyReturn: (13.8 / 251).toFixed(2)
  },
  vanguardRealEstate: {
    monthlyReturn: -0.38,
    sharpeRatio: 0.32,
    sortinoRatio: 0.41,
    meanDailyReturn: (0.12 / 251).toFixed(2)
  },
  eurekaHedge: {
    monthlyReturn: 0.42,
    sharpeRatio: 0.75,
    sortinoRatio: 1.02,
    meanDailyReturn: (3.89 / 251).toFixed(2)
  }
};

const monthlyBarData = performanceData.map((item) => {
  const monthlyReturn = {
    month: item.month.split(' ')[0],
    'DEL PHI': item.month === 'Dec 2022' ? item.fund : 
      ((item.fund - performanceData[performanceData.findIndex(d => d.month === item.month) - 1].fund) / 
      (1 + performanceData[performanceData.findIndex(d => d.month === item.month) - 1].fund / 100)),
    'S&P 500': item.month === 'Dec 2022' ? item['S&P 500'] : 
      ((item['S&P 500'] - performanceData[performanceData.findIndex(d => d.month === item.month) - 1]['S&P 500']) / 
      (1 + performanceData[performanceData.findIndex(d => d.month === item.month) - 1]['S&P 500'] / 100)),
    'Vanguard Real Estate': item.month === 'Dec 2022' ? item['Vanguard Real Estate Index'] : 
      ((item['Vanguard Real Estate Index'] - performanceData[performanceData.findIndex(d => d.month === item.month) - 1]['Vanguard Real Estate Index']) / 
      (1 + performanceData[performanceData.findIndex(d => d.month === item.month) - 1]['Vanguard Real Estate Index'] / 100)),
    'EurekaHedge HF Index': item.month === 'Dec 2022' ? item['EurekaHedge Hedge Fund Index'] : 
      ((item['EurekaHedge Hedge Fund Index'] - performanceData[performanceData.findIndex(d => d.month === item.month) - 1]['EurekaHedge Hedge Fund Index']) / 
      (1 + performanceData[performanceData.findIndex(d => d.month === item.month) - 1]['EurekaHedge Hedge Fund Index'] / 100))
  };
  
  return monthlyReturn;
});

const finalValues = (() => {
  const lastItem = performanceData[performanceData.length - 1];
  return [
    { name: 'DEL PHI', value: `+${lastItem.fund.toFixed(2)}%`, color: '#ea384c' },
    { name: 'S&P 500', value: `${lastItem['S&P 500'] >= 0 ? '+' : ''}${lastItem['S&P 500'].toFixed(2)}%`, color: '#8884d8' },
    { name: 'Vanguard Real Estate', value: `${lastItem['Vanguard Real Estate Index'] >= 0 ? '+' : ''}${lastItem['Vanguard Real Estate Index'].toFixed(2)}%`, color: '#82ca9d' },
    { name: 'EurekaHedge HF Index', value: `${lastItem['EurekaHedge Hedge Fund Index'] >= 0 ? '+' : ''}${lastItem['EurekaHedge Hedge Fund Index'].toFixed(2)}%`, color: '#ffc658' }
  ];
})();

const Performance = () => {
  return <div className="min-h-screen flex flex-col bg-delphi-dark text-delphi-light">
      <Navbar />
      
      <main className="flex-grow pt-20">
        <section className="py-20 relative overflow-hidden grid-bg">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">Real-Money Track Record</h1>
              <p className="text-xl text-delphi-light/70 mb-8">
                Several prototype versions of the strategy were tested and tweaked over 12 consecutive months of trading, with an average capital pool of ~$100,000, returning over 130% after fees with a near-zero correlation to benchmarks.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <RequestButton 
                  label="Request Detailed Performance Report" 
                  requestType="Performance Report Request"
                  className="inline-flex items-center px-4 py-2 bg-delphi-red text-white rounded text-sm hover:bg-red-600 transition-colors"
                />
              </div>
            </div>
          </div>
        </section>
        
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-8">Performance Overview</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
                <div className="data-section p-6 bg-gradient-to-br from-delphi-gray/20 to-transparent">
                  <div className="flex items-center mb-3">
                    <TrendingUp size={20} className="text-delphi-red mr-2" />
                    <h3 className="text-lg font-bold">Monthly Return</h3>
                  </div>
                  <p className="text-3xl font-bold text-delphi-red">{stats.delPhi.monthlyReturn}%</p>
                  <div className="text-delphi-light/60 text-sm mt-2">
                    Average monthly return
                    <div className="flex flex-col mt-1 space-y-1">
                      <span className="text-xs" style={{ color: '#8884d8' }}>S&P 500: {stats.sp500.monthlyReturn}%</span>
                      <span className="text-xs" style={{ color: '#82ca9d' }}>Vanguard RE: {stats.vanguardRealEstate.monthlyReturn}%</span>
                      <span className="text-xs" style={{ color: '#ffc658' }}>EurekaHedge: {stats.eurekaHedge.monthlyReturn}%</span>
                    </div>
                  </div>
                </div>
                
                <div className="data-section p-6 bg-gradient-to-br from-delphi-gray/20 to-transparent">
                  <div className="flex items-center mb-3">
                    <BarChart3 size={20} className="text-delphi-red mr-2" />
                    <h3 className="text-lg font-bold">Mean Daily Return</h3>
                  </div>
                  <p className="text-3xl font-bold text-delphi-red">{stats.delPhi.meanDailyReturn}%</p>
                  <div className="text-delphi-light/60 text-sm mt-2">
                    Average return per trading day
                    <div className="flex flex-col mt-1 space-y-1">
                      <span className="text-xs" style={{ color: '#8884d8' }}>S&P 500: {stats.sp500.meanDailyReturn}%</span>
                      <span className="text-xs" style={{ color: '#82ca9d' }}>Vanguard RE: {stats.vanguardRealEstate.meanDailyReturn}%</span>
                      <span className="text-xs" style={{ color: '#ffc658' }}>EurekaHedge: {stats.eurekaHedge.meanDailyReturn}%</span>
                    </div>
                  </div>
                </div>
                
                <div className="data-section p-6 bg-gradient-to-br from-delphi-gray/20 to-transparent">
                  <div className="flex items-center mb-3">
                    <Percent size={20} className="text-delphi-red mr-2" />
                    <h3 className="text-lg font-bold">Sharpe Ratio</h3>
                  </div>
                  <p className="text-3xl font-bold text-delphi-red">{stats.delPhi.sharpeRatio}</p>
                  <div className="text-delphi-light/60 text-sm mt-2">
                    Excellent risk-adjusted returns
                    <div className="flex flex-col mt-1 space-y-1">
                      <span className="text-xs" style={{ color: '#8884d8' }}>S&P 500: {stats.sp500.sharpeRatio}</span>
                      <span className="text-xs" style={{ color: '#82ca9d' }}>Vanguard RE: {stats.vanguardRealEstate.sharpeRatio}</span>
                      <span className="text-xs" style={{ color: '#ffc658' }}>EurekaHedge: {stats.eurekaHedge.sharpeRatio}</span>
                    </div>
                  </div>
                </div>
                
                <div className="data-section p-6 bg-gradient-to-br from-delphi-gray/20 to-transparent">
                  <div className="flex items-center mb-3">
                    <AlertTriangle size={20} className="text-delphi-red mr-2" />
                    <h3 className="text-lg font-bold">Sortino Ratio</h3>
                  </div>
                  <p className="text-3xl font-bold text-delphi-red">{stats.delPhi.sortinoRatio}</p>
                  <div className="text-delphi-light/60 text-sm mt-2">
                    Strong downside risk management
                    <div className="flex flex-col mt-1 space-y-1">
                      <span className="text-xs" style={{ color: '#8884d8' }}>S&P 500: {stats.sp500.sortinoRatio}</span>
                      <span className="text-xs" style={{ color: '#82ca9d' }}>Vanguard RE: {stats.vanguardRealEstate.sortinoRatio}</span>
                      <span className="text-xs" style={{ color: '#ffc658' }}>EurekaHedge: {stats.eurekaHedge.sortinoRatio}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mb-16">
                <div className="bg-delphi-dark border border-delphi-gray/20 rounded-lg p-6 shadow-lg">
                  <div className="mb-2">
                    <h3 className="text-xl font-semibold flex items-center">
                      <TrendingUp size={20} className="text-delphi-red mr-2" />
                      <span>12-Month Cumulative Return (%)</span>
                    </h3>
                    <p className="text-delphi-light/60 text-sm mt-1">December 1st, 2022 to December 4th, 2023</p>
                  </div>
                  
                  <div className="bg-delphi-gray/20 rounded-lg p-3 mb-4 inline-block">
                    <div className="flex flex-wrap gap-4">
                      {finalValues.map((item, index) => (
                        <div key={index} className="flex items-center">
                          <div className="w-3 h-3 rounded-sm mr-2" style={{ backgroundColor: item.color }}></div>
                          <span className="text-sm mr-1">{item.name}:</span>
                          <span className="text-sm font-bold" style={{ color: item.color }}>{item.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="h-80 relative">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={performanceData} margin={{ top: 5, right: 90, left: 0, bottom: 5 }}>
                        <defs>
                          <pattern id="diagonalLines" patternUnits="userSpaceOnUse" width="4" height="4" patternTransform="rotate(45)">
                            <line x1="0" y1="0" x2="0" y2="4" stroke="#ea384c" strokeWidth="1" strokeOpacity="0.2" />
                          </pattern>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                        <XAxis dataKey="month" stroke="#8E9196" />
                        <YAxis stroke="#8E9196" domain={[-15, 140]} />
                        <Tooltip contentStyle={{
                          backgroundColor: '#1A1F2C',
                          borderColor: '#2a2a2a',
                          color: '#ffffff'
                        }} />
                        <Area
                          type="monotone"
                          dataKey="fund"
                          stroke="none"
                          fill="url(#diagonalLines)"
                        />
                        <Line
                          type="monotone"
                          dataKey="fund"
                          name="DEL PHI"
                          stroke="#ea384c"
                          strokeWidth={3}
                          dot={{ r: 4 }}
                          activeDot={{ r: 6 }}
                        />
                        <Line
                          type="monotone"
                          dataKey="S&P 500"
                          name="S&P 500"
                          stroke="#8884d8"
                          strokeWidth={2}
                          dot={{ r: 3 }}
                        />
                        <Line
                          type="monotone"
                          dataKey="Vanguard Real Estate Index"
                          name="Vanguard Real Estate Index"
                          stroke="#82ca9d"
                          strokeWidth={2}
                          dot={{ r: 3 }}
                        />
                        <Line
                          type="monotone"
                          dataKey="EurekaHedge Hedge Fund Index"
                          name="EurekaHedge Hedge Fund Index"
                          stroke="#ffc658"
                          strokeWidth={2}
                          dot={{ r: 3 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                
                <p className="text-delphi-light/70 text-sm italic mb-4">
                  Note: This chart displays performance assessed on the last trading day of each month. Daily performance charts, 
                  which showcase higher volatility, are available by request.
                </p>
              </div>
              
              <div className="mb-16">
                <div className="bg-delphi-dark border border-delphi-gray/20 rounded-lg p-6 mb-6 shadow-lg">
                  <div className="flex items-center mb-6">
                    <Calendar size={20} className="text-delphi-red mr-2" />
                    <h3 className="text-xl font-semibold">Monthly Performance (%) - 2023</h3>
                  </div>
                  
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={monthlyBarData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                        <XAxis dataKey="month" stroke="#8E9196" />
                        <YAxis stroke="#8E9196" />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#1A1F2C',
                            borderColor: '#2a2a2a',
                            color: '#ffffff'
                          }}
                          formatter={(value: number) => [`${value.toFixed(2)}%`, '']}
                        />
                        <Legend />
                        <Bar dataKey="DEL PHI" name="DEL PHI" fill="#ea384c" />
                        <Bar dataKey="S&P 500" name="S&P 500" fill="#8884d8" />
                        <Bar dataKey="Vanguard Real Estate" name="Vanguard Real Estate" fill="#82ca9d" />
                        <Bar dataKey="EurekaHedge HF Index" name="EurekaHedge HF Index" fill="#ffc658" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                
                <p className="text-delphi-light/70 text-sm italic">
                  Monthly performance demonstrates the consistency of our strategy even during market turbulence.
                </p>
              </div>
              
              <div className="mb-16">
                <div className="bg-delphi-dark border border-delphi-gray/20 rounded-lg p-6 shadow-lg">
                  <h3 className="text-xl font-semibold mb-4">Market Correlation Analysis</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <div className="bg-delphi-gray/20 rounded-lg p-6">
                        <h4 className="text-5xl font-bold text-delphi-red mb-2">-0.08</h4>
                        <p className="text-lg font-medium mb-2">Average Correlation to Benchmarks</p>
                        <p className="text-delphi-light/70">
                          Our near-zero correlation demonstrates the strategy's independence from traditional market movements, allowing for consistent performance regardless of market conditions.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <div className="space-y-4">
                        <h4 className="font-medium">What this means:</h4>
                        <ul className="list-disc pl-5 space-y-2 text-delphi-light/70">
                          <li>Strategy performs independently of market direction</li>
                          <li>Excellent portfolio diversification potential</li>
                          <li>Consistent returns through various market cycles</li>
                          <li className="text-delphi-red animate-pulse">True alpha generation, unrelated to market beta</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold mb-6">Strategy Insights</h3>
                
                <div className="data-section p-6 md:p-8 bg-gradient-to-br from-delphi-gray/20 to-transparent backdrop-blur-sm mb-8">
                  <p className="text-delphi-light/70 mb-6">
                    The strategy is reliant only on free, easily accessible data that is explicitly quantitative. If a stock's options chain is sufficiently saturated it qualifies as a tradable target. This means there are countless viable targets, including high volume equities listed on exchanges around the world.
                  </p>
                  
                  <div className="mb-6">
                    <h4 className="text-lg font-medium mb-2">Key Performance Drivers</h4>
                    <ul className="list-disc pl-5 space-y-2 text-delphi-light/70">
                      <li>Near-zero correlation to benchmarks like the S&P 500 and Vanguard Real Estate Index</li>
                      <li>All 12 consecutive months saw positive returns</li>
                      <li>Successfully tested at smaller scales ($1k-$100k)</li>
                      <li>Simulation suggests appropriate scaling to ~$10MM account value</li>
                    </ul>
                  </div>
                  
                  <p className="text-delphi-light/70 font-medium mt-6">
                    No politics. No economics. No financials.
                  </p>
                </div>
              </div>
              
              <div className="p-6 bg-delphi-red/5 border border-delphi-red/20 rounded-lg">
                <h3 className="text-xl font-medium flex items-center mb-4">
                  <AlertTriangle size={20} className="mr-2" />
                  <span>SEC Disclaimer</span>
                </h3>
                <p className="text-delphi-light/70 text-sm">
                  Information found on this site is intended only for Accredited, Qualified, or Institutional Investors as defined by the S.E.C.
                </p>
              </div>
            </div>
          </div>
        </section>
        
        {/* Stylized Divider */}
        <StylizedDivider className="py-16" />
        
        {/* Paper Trading Results Section */}
        <PaperTradingResults />
        
      </main>
      
      <Footer />
    </div>;
};

export default Performance;
